import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../config/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Track page view
    axios.post(`${API_URL}/api/track`, { page: 'contact', action: 'page_view' }).catch(() => {});
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${API_URL}/api/contact`, formData);
      setStatus({ type: 'success', message: response.data.message });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.response?.data?.message || 'Failed to send message. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#f8f3e8] min-h-screen py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 text-[#1f3a27]" style={{ fontFamily: 'Georgia, serif' }}>Contact Us</h1>
          <p className="text-xl text-gray-600">
            Get in touch with us for orders, inquiries, or more information about our products.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Information */}
          <div className="bg-[#fffdf8] rounded-2xl border border-[#e7decb] shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6 text-[#1f3a27]" style={{ fontFamily: 'Georgia, serif' }}>Get in Touch</h2>
            
            <div className="space-y-6 mb-8">
              <div>
                <h3 className="font-semibold text-lg mb-2">Address</h3>
                <p className="text-gray-700">
                  No.21 Shop No. 2, 2nd Main, 4th Cross Royal Colony<br />
                  Near Chowdeshwari Bar, Medarahalli, Chikkabanavara Post<br />
                  Bengaluru - 560090, Karnataka
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-2">Phone</h3>
                <a href="tel:+919902043782" className="text-[#1f3a27] hover:underline">
                  +91 99020 43782
                </a>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-2">Email</h3>
                <a href="mailto:admin@earthlyspiceexports.com" className="text-[#1f3a27] hover:underline">
                  admin@earthlyspiceexports.com
                </a>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-4">WhatsApp</h3>
                <a
                  href="https://wa.me/919902043782?text=Hi, I'm interested in your spices. Please share more details."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-[#1f3a27] text-white px-6 py-3 rounded-full text-sm font-semibold tracking-wide uppercase hover:bg-[#17301f] transition-colors"
                >
                  Message us on WhatsApp
                </a>
              </div>
            </div>

            {/* Google Maps */}
            <div className="mt-8">
              <h3 className="font-semibold text-lg mb-4">Find Us on Map</h3>
              <div className="w-full h-64 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center">
                <iframe
                  title="Earthly Spice Exports Location"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  src="https://maps.google.com/maps?q=No.+21+Shop+No.+2,+2nd+Main,+4th+Cross+Royal+Colony,+Near+Chowdeshwari+Bar,+Medarahalli,+Chikkabanavara+Post,+Bengaluru,+560090,+Karnataka&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                <a
                  href="https://www.google.com/maps/search/?api=1&query=No.+21+Shop+No.+2,+2nd+Main,+4th+Cross+Royal+Colony,+Near+Chowdeshwari+Bar,+Medarahalli,+Chikkabanavara+Post,+Bengaluru,+560090,+Karnataka"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#1f3a27] hover:underline"
                >
                  Open in Google Maps
                </a>
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-[#fffdf8] rounded-2xl border border-[#e7decb] shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6 text-[#1f3a27]" style={{ fontFamily: 'Georgia, serif' }}>Send us a Message</h2>

            {status.message && (
              <div
                className={`mb-6 p-4 rounded-lg ${
                  status.type === 'success'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {status.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-[#d8d0bf] rounded-xl focus:ring-2 focus:ring-[#35533a]/30 focus:border-[#35533a] outline-none"
                  placeholder="Your Name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-[#d8d0bf] rounded-xl focus:ring-2 focus:ring-[#35533a]/30 focus:border-[#35533a] outline-none"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-[#d8d0bf] rounded-xl focus:ring-2 focus:ring-[#35533a]/30 focus:border-[#35533a] outline-none"
                  placeholder="+91 12345 67890"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-[#d8d0bf] rounded-xl focus:ring-2 focus:ring-[#35533a]/30 focus:border-[#35533a] outline-none"
                  placeholder="Your message..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#1f3a27] text-white py-3 rounded-full text-sm font-semibold tracking-wide uppercase hover:bg-[#17301f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
